<template>
  <ConfigPage>
    <ConfigItem>
      <v-text-field
        v-model="search"
        @keyup.enter="loadItems"
        solo
        :label="__('pesquisar')"
        single-line
        prepend-inner-icon="mdi-magnify"
        hide-details
        class="mb-2"
        clearable
      >
        <template #append-outer>
          <v-btn @click="loadItems()" icon>
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </template>
      </v-text-field>
      <v-toolbar v-if="selected.length" flat>
        <v-toolbar-title>
          {{ selected.length }} {{ __("itens carregados") }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <ScopeProvider scope="configs.addresses.postal_codes.edit">
          <v-btn @click="selected = []" small text>
            <v-icon left>mdi-close</v-icon> {{ __("cancelar") }}
          </v-btn>
          <v-btn @click="handleClickDelete()" color="red" dark small text>
            <v-icon left>mdi-delete</v-icon> {{ __("excluir") }}</v-btn
          >
        </ScopeProvider>
      </v-toolbar>
      <v-toolbar v-else flat>
        <v-toolbar-title v-text="__('endereços', 'settings')" />
        <v-spacer></v-spacer>

        <ScopeProvider scope="configs.addresses.postal_codes.edit">
          <v-btn @click="handleClickAdd()" color="primary">
            {{ __("registrar") }}
          </v-btn>
        </ScopeProvider>
      </v-toolbar>
      <v-toolbar flat dense class="px-0">
        <v-switch
          v-model="showOnlyManual"
          :label="
            __('mostrar apenas endereços cadastrados manualmente', 'settings')
          "
          hide-details
          inset
          @change="loadItems"
        />
      </v-toolbar>

      <v-sheet class="px-4">
        <v-chip-group column>
          <v-chip v-for="item in selected" :key="item.id" small>
            {{ item.street }}
          </v-chip>
        </v-chip-group>
      </v-sheet>

      <v-data-table
        v-if="options"
        color="primary"
        :value="selected"
        @input="handleSelectionChange"
        show-select
        :items="items"
        :headers="headers"
        must-sort
        :options.sync="options"
        :server-items-length="totalItems"
        :footer-props="{
          itemsPerPageOptions: [5, 10, 15, 50],
        }"
      >
        <template #item.data-table-select="{ item, isSelected, select }">
          <v-simple-checkbox
            v-if="item.is_manual_registration"
            :value="isSelected"
            @click.stop="select(!isSelected)"
          />
          <template v-else>
            <v-tooltip bottom>
              <template #activator="{ on }">
                <v-icon small color="grey" class="v-simple-checkbox" v-on="on">
                  mdi-checkbox-blank-off-outline
                </v-icon>
              </template>
              <span>{{
                __("não é possível selecionar este endereço", "settings")
              }}</span>
            </v-tooltip>
          </template>
        </template>
        <template #header.data-table-select>
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-simple-checkbox
                v-bind="attrs"
                v-on="on"
                :indeterminate="someManualSelected && !allManualSelected"
                :value="allManualSelected"
                @click.stop="manualItems.length && toggleSelectAll()"
                :disabled="manualItems.length === 0"
              />
            </template>
            <span>
              {{
                manualItems.length === 0
                  ? __(
                      "nenhum endereço manual disponível para seleção",
                      "settings"
                    )
                  : allManualSelected
                  ? __(
                      "desmarcar todos os endereços cadastrados manualmente",
                      "settings"
                    )
                  : __(
                      "selecionar todos os endereços cadastrados manualmente",
                      "settings"
                    )
              }}
            </span>
          </v-tooltip>
        </template>

        <template #item="{ item, select, isSelected }">
          <tr
            :class="{ 'v-data-table__selected': isSelected }"
            @click="item.is_manual_registration && handleUpdateItem(item)"
            :style="{
              cursor: item.is_manual_registration ? 'pointer' : 'default',
            }"
          >
            <td>
              <template v-if="item.is_manual_registration">
                <v-simple-checkbox
                  :value="isSelected"
                  @click.stop="select(!isSelected)"
                />
              </template>
              <template v-else>
                <v-tooltip bottom>
                  <template #activator="{ on }">
                    <v-simple-checkbox
                      disabled
                      on-icon="mdi-checkbox-blank-off-outline"
                      off-icon="mdi-checkbox-blank-off-outline"
                      class="grey--text"
                      v-on="on"
                    />
                  </template>
                  <span>{{
                    __("não é possível selecionar este endereço", "settings")
                  }}</span>
                </v-tooltip>
              </template>
            </td>
            <td>
              <ScopeProvider scope="configs.addresses.postal_codes.edit">
                {{ item.postal_code }}
              </ScopeProvider>
            </td>
            <td>
              <ScopeProvider scope="configs.addresses.postal_codes.edit">
                {{ item.country_area.name }}
              </ScopeProvider>
            </td>
            <td>
              <ScopeProvider scope="configs.addresses.postal_codes.edit">
                {{ item.city.name }}
              </ScopeProvider>
            </td>
            <td>
              <ScopeProvider scope="configs.addresses.postal_codes.edit">
                {{ item.city_area.name }}
              </ScopeProvider>
            </td>
            <td>
              <ScopeProvider scope="configs.addresses.postal_codes.edit">
                {{ item.street }}
              </ScopeProvider>
            </td>
          </tr>
        </template>
      </v-data-table>
    </ConfigItem>

    <ConfirmationDialog ref="confirmDialog" />
    <PostalCodeFormDialog ref="formDialog" @submitted="loadItems()" />
    <v-overlay :value="loading" absolute>
      <v-progress-circular indeterminate color="primary" size="64" />
    </v-overlay>
  </ConfigPage>
</template>

<script>
import ConfigPage from "@/layouts/configs/ConfigPage";
import ConfigItem from "@/layouts/configs/ConfigItem";
import i18n from "@/mixins/i18n";
import data_table from "@/tools/data_table";
import { indexWithPagination, destroy } from "@/services/locals";
import ToastsMixin from "@/mixins/ToastsMixin.vue";
import ConfirmationDialog from "@/components/tools/ConfirmationDialog.vue";
import PostalCodeFormDialog from "@/components/PostalCodes/PostalCodeFormDialog.vue";
import ScopeProvider from "@/components/ScopeProvider";

export default {
  mixins: [i18n, ToastsMixin],
  components: {
    ConfigPage,
    ConfigItem,
    ConfirmationDialog,
    PostalCodeFormDialog,
    ScopeProvider,
  },

  data() {
    return {
      i18n_prefix: "settings",
      items: [],
      selected: [],
      options: null,
      totalItems: 0,
      search: "",
      headers: [
        { text: this.__("CEP", "settings"), value: "postal_code" },
        { text: this.__("estado", "settings"), value: "country_areas.name" },
        { text: this.__("cidade", "settings"), value: "city_name" },
        { text: this.__("bairro", "settings"), value: "city_areas.name" },
        { text: this.__("rua", "settings"), value: "street" },
      ],
      showOnlyManual: false,
      loading: false,
    };
  },

  methods: {
    handleClickAdd() {
      this.$refs.formDialog.open();
    },

    handleUpdateItem(item) {
      this.$refs.formDialog.openForEdit(item);
    },

    handleSelectionChange(newSelection) {
      this.selected = newSelection.filter(
        (item) => item.is_manual_registration
      );
    },

    toggleSelectAll() {
      const manualItems = this.items.filter(
        (item) => item.is_manual_registration
      );
      const selectedIds = this.selected.map((item) => item.id);

      const allManualSelected = manualItems.every((item) =>
        selectedIds.includes(item.id)
      );

      if (allManualSelected) {
        this.selected = this.selected.filter(
          (item) => !item.is_manual_registration
        );
      } else {
        const newSelection = manualItems.filter(
          (item) => !selectedIds.includes(item.id)
        );
        this.selected = [...this.selected, ...newSelection];
      }
    },

    async loadItems(options = null) {
      if (!options) {
        options = this.options;
      }

      this.loading = true;
      this.items = [];

      try {
        const response = await indexWithPagination("PT", {
          ...data_table.getParams(options, this.search),
          only_manual: this.showOnlyManual ? 1 : 0,
        });

        this.items = response.data;
        this.totalItems = response.meta.total;
      } catch (error) {
        this.cError(
          this.__(
            "não foi possível carregar endereços, tente novamente mais tarde",
            "settings"
          )
        );
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    handleClickDelete() {
      this.$refs.confirmDialog.confirm(
        this.__("excluir os itens carregados?", "settings"),
        async ({ close, startLoading, stopLoading }) => {
          startLoading();
          try {
            await this.destroySelected();
            close();
            this.toast(this.__("itens removidos", "settings"));
            this.selected = [];
            this.loadItems();
          } catch (error) {
            if (error.response?.status === 400) {
              this.toastError(error.response.data.message);
              return;
            }
            this.toastError(
              this.__("não foi possível excluir os endereços", "settings")
            );
          } finally {
            stopLoading();
          }
        },
        null,
        {
          color: "red",
          confirmText: this.__("excluir", "settings"),
          description: `
    <ul>
      <li>${this.__("apaga todos os endereços selecionados", "settings")}</li>
      <li>${this.__("não é possível reverter", "settings")}</li>
    </ul>
  `,
        }
      );
    },

    destroySelected() {
      const manualIds = this.selected
        .filter((item) => item.is_manual_registration)
        .map((item) => item.id);

      return destroy({ ids: manualIds });
    },
  },

  watch: {
    options: {
      handler(newOptions) {
        this.loadItems(newOptions);
      },
      deep: true,
    },
  },

  created() {
    this.options = data_table.getOptions();
    this.loadItems(this.options);
  },

  computed: {
    manualItems() {
      return this.items.filter((item) => item.is_manual_registration);
    },
    selectedManualItems() {
      return this.selected.filter((item) => item.is_manual_registration);
    },
    allManualSelected() {
      return (
        this.manualItems.length > 0 &&
        this.selectedManualItems.length === this.manualItems.length
      );
    },
    someManualSelected() {
      return (
        this.selectedManualItems.length > 0 &&
        this.selectedManualItems.length < this.manualItems.length
      );
    },
  },
};
</script>
